/**
 * generateO365PlatformDoc.ts - AutoDoc (aperçu).
 *
 * Génère un document Word "Documentation Plateforme Microsoft 365" à partir du
 * dernier scan M365 + des informations d'organisation. Objectif : produire une
 * base documentaire de conformité NIS2 (art. 21) directement exploitable.
 *
 * V1 : 100% côté navigateur, à partir des constats du scan (statut + preuve
 * texte par contrôle). V2 (backend Graph) enrichira avec des faits nominatifs
 * (app registrations, résidence des données, politiques CA détaillées).
 */
import {
  Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun,
  HeadingLevel, WidthType,
} from 'docx';
import { saveAs } from 'file-saver';
import { controls, familyLabels } from '../domain/controls';
import type { ControlDefinition } from '../domain/types';
import type { PlatformFacts, AppRegistration } from '../api/platformFactsApi';
import { buildCover, buildToc } from './autodocCommon';

export interface O365DocMeta {
  orgName:       string;
  tenantDomain?: string;
  tenantId?:     string;
  lastScanDate?: string;
  generatedBy?:  string;
  logo?:         string | null;
}

export interface O365ScanHit { status: string; message?: string; date?: string; }

const BRAND = '1F3D5C';
const ACCENT = '2E8B96';

function statusFr(s?: string): string {
  const v = (s ?? '').toLowerCase();
  if (v === 'pass') return 'Conforme';
  if (v === 'fail' || v === 'error') return 'Non conforme';
  if (v === 'warning' || v === 'warn') return 'À améliorer';
  if (v === 'manual' || v === 'skipped') return 'À vérifier (manuel)';
  return 'Non évalué';
}
function statusColor(s?: string): string {
  const v = (s ?? '').toLowerCase();
  if (v === 'pass') return '1F7A45';
  if (v === 'fail' || v === 'error') return 'B82329';
  if (v === 'warning' || v === 'warn') return 'A86317';
  if (v === 'manual' || v === 'skipped') return '4A4A6E';
  return '7A7A7A';
}
function fmtDate(d?: string): string {
  if (!d) return '';
  const x = new Date(d);
  return isNaN(x.getTime()) ? '' : x.toLocaleDateString('fr-BE');
}
function today(): string { return new Date().toLocaleDateString('fr-BE'); }
function safeName(s: string): string { return (s || 'organisation').replace(/[^\w]+/g, '_'); }

// --- Cellules de tableau -----------------------------------------------------
function cell(text: string, opts?: { bold?: boolean; color?: string; width?: number }): TableCell {
  return new TableCell({
    width: opts?.width ? { size: opts.width, type: WidthType.PERCENTAGE } : undefined,
    children: [new Paragraph({ children: [new TextRun({ text: text ?? '', bold: opts?.bold, color: opts?.color, size: 18 })] })],
  });
}
function headerCell(text: string, width?: number): TableCell {
  return new TableCell({
    width: width ? { size: width, type: WidthType.PERCENTAGE } : undefined,
    shading: { fill: BRAND },
    children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 18 })] })],
  });
}

// Thèmes NIS2 mis en avant (heuristique sur l'intitulé des contrôles).
function controlRow(c: ControlDefinition, hit?: O365ScanHit): TableRow {
  const constat = hit?.message?.trim() || (hit ? '—' : 'Non couvert par le dernier scan');
  return new TableRow({ children: [
    cell(c.id, { bold: true, width: 14 }),
    cell(c.title, { width: 42 }),
    cell(statusFr(hit?.status), { bold: true, color: statusColor(hit?.status), width: 18 }),
    cell(constat, { width: 26 }),
  ] });
}
function controlTable(rows: ControlDefinition[], scanByControl: Map<string, O365ScanHit>): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({ tableHeader: true, children: [
        headerCell('ID', 14), headerCell('Élément de configuration', 42),
        headerCell('État', 18), headerCell('Constat / preuve', 26),
      ] }),
      ...rows.map((c) => controlRow(c, scanByControl.get(c.id.toLowerCase()))),
    ],
  });
}

// ---------------------------------------------------------------------------
// Annexe A — faits nominatifs récupérés en direct via Microsoft Graph (V2).
// ---------------------------------------------------------------------------
function credLabel(app: AppRegistration): string {
  if (app.hasExpiredCred) return 'Credential EXPIRÉ';
  const days = app.credentials.map((c) => c.daysToExpiry).filter((d): d is number => typeof d === 'number');
  if (days.length === 0) return 'Aucun credential';
  const min = Math.min(...days);
  return min <= 30 ? `Expire dans ${min} j` : `OK (${min} j)`;
}
function credColor(app: AppRegistration): string {
  if (app.hasExpiredCred) return 'B82329';
  if (app.hasSoonCred) return 'A86317';
  return '1F7A45';
}

function buildGraphAnnex(facts: PlatformFacts): (Paragraph | Table)[] {
  const out: (Paragraph | Table)[] = [
    new Paragraph({ text: 'Annexe A — Faits de plateforme (Microsoft Graph)', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
    new Paragraph({ children: [new TextRun({ text: 'Données lues en direct sur le tenant via Microsoft Graph (lecture seule).', italics: true, size: 20, color: '666666' })] }),
  ];

  // A.1 Organisation
  if (facts.organization) {
    const o = facts.organization;
    const domains = (o.verifiedDomains ?? []).map((d) => d.name + (d.isDefault ? ' (par défaut)' : '') + (d.isInitial ? ' (initial)' : '')).join(', ');
    const rows: [string, string][] = [
      ['Organisation', o.displayName || '—'],
      ['Tenant ID', o.tenantId || '—'],
      ['Pays', o.country || '—'],
      ['Créé le', fmtDate(o.createdDateTime) || '—'],
      ['Synchronisation AD locale', o.onPremisesSyncEnabled ? 'Oui (hybride)' : 'Non (cloud only)'],
      ['Domaines vérifiés', domains || '—'],
    ];
    out.push(new Paragraph({ text: 'A.1 Organisation & domaines', heading: HeadingLevel.HEADING_3 }));
    out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: rows.map(([k, v]) => new TableRow({ children: [headerCell(k, 32), cell(v)] })) }));
    out.push(new Paragraph({ children: [] }));
  }

  // A.2 Comptes & rôles
  if (facts.users || facts.globalAdmins) {
    const rows: [string, string][] = [
      ['Utilisateurs (total)', facts.users?.total != null ? String(facts.users.total) : '—'],
      ['Invités (guests)', facts.users?.guests != null ? String(facts.users.guests) : '—'],
      ['Global Administrators', facts.globalAdmins?.count != null ? String(facts.globalAdmins.count) : '—'],
    ];
    out.push(new Paragraph({ text: 'A.2 Comptes & rôles privilégiés', heading: HeadingLevel.HEADING_3 }));
    out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: rows.map(([k, v]) => new TableRow({ children: [headerCell(k, 40), cell(v)] })) }));
    out.push(new Paragraph({ children: [] }));
  }

  // A.3 App registrations + expiration des credentials
  if (facts.appRegistrations) {
    const ar = facts.appRegistrations;
    out.push(new Paragraph({ text: 'A.3 Applications (App Registrations) & expiration des secrets', heading: HeadingLevel.HEADING_3 }));
    out.push(new Paragraph({ children: [new TextRun({ text: `${ar.count} application(s) — ${ar.expiredCredApps} avec credential expiré · ${ar.soonCredApps} expirant sous 30 jours.`, size: 20 })] }));
    out.push(new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({ tableHeader: true, children: [headerCell('Application', 40), headerCell('App ID', 30), headerCell('Audience', 14), headerCell('Credential', 16)] }),
        ...ar.items.map((a) => new TableRow({ children: [
          cell(a.displayName || '—'),
          cell(a.appId || '—'),
          cell(a.signInAudience || '—'),
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: credLabel(a), bold: true, color: credColor(a), size: 18 })] })] }),
        ] })),
      ],
    }));
    out.push(new Paragraph({ children: [] }));
  }

  // A.4 Conditional Access
  if (facts.conditionalAccess) {
    const ca = facts.conditionalAccess;
    out.push(new Paragraph({ text: 'A.4 Politiques d’accès conditionnel', heading: HeadingLevel.HEADING_3 }));
    out.push(new Paragraph({ children: [new TextRun({ text: `${ca.count} politique(s) — ${ca.enabled} active(s).`, size: 20 })] }));
    out.push(new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({ tableHeader: true, children: [headerCell('Politique', 75), headerCell('État', 25)] }),
        ...ca.items.map((p) => new TableRow({ children: [
          cell(p.displayName || '—'),
          cell(p.state === 'enabled' ? 'Activée' : p.state === 'disabled' ? 'Désactivée' : p.state === 'enabledForReportingButNotEnforced' ? 'Report-only' : (p.state || '—'),
            { color: p.state === 'enabled' ? '1F7A45' : '7A7A7A', bold: true }),
        ] })),
      ],
    }));
    out.push(new Paragraph({ children: [] }));
  }

  // A.5 Licences
  if (facts.licenses) {
    out.push(new Paragraph({ text: 'A.5 Licences (SKUs souscrits)', heading: HeadingLevel.HEADING_3 }));
    out.push(new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({ tableHeader: true, children: [headerCell('SKU', 60), headerCell('Activées', 20), headerCell('Consommées', 20)] }),
        ...facts.licenses.items.map((s) => new TableRow({ children: [
          cell(s.skuPartNumber || '—'), cell(String(s.enabled)), cell(String(s.consumed)),
        ] })),
      ],
    }));
    out.push(new Paragraph({ children: [] }));
  }

  return out;
}

export async function downloadO365PlatformDocx(params: {
  meta: O365DocMeta;
  scanByControl: Map<string, O365ScanHit>;
  facts?: PlatformFacts | null;
}): Promise<void> {
  const { meta, scanByControl, facts } = params;

  // Regroupe les contrôles M365 par famille.
  const byFamily = new Map<string, ControlDefinition[]>();
  for (const c of controls) {
    const arr = byFamily.get(c.family) ?? [];
    arr.push(c);
    byFamily.set(c.family, arr);
  }

  // Compteurs synthèse.
  let nPass = 0, nFail = 0, nWarn = 0, nManual = 0, nOther = 0, nCovered = 0;
  for (const c of controls) {
    const h = scanByControl.get(c.id.toLowerCase());
    if (!h) { nOther++; continue; }
    nCovered++;
    const s = (h.status ?? '').toLowerCase();
    if (s === 'pass') nPass++;
    else if (s === 'fail' || s === 'error') nFail++;
    else if (s === 'warning' || s === 'warn') nWarn++;
    else if (s === 'manual' || s === 'skipped') nManual++;
    else nOther++;
  }

  // --- Page de garde + introduction (mise en forme partagée) ---------------
  const cover = await buildCover({
    brandLine: 'NIS365 AUDITOR · AutoDoc',
    title: 'Documentation Plateforme Microsoft 365',
    subtitle: 'Base documentaire de conformité NIS2 (art. 21)',
    orgName: meta.orgName,
    logo: meta.logo,
    footnote: 'Document généré automatiquement (scan + Microsoft Graph).',
  });
  void byFamily; void familyLabels; // (regroupement remplacé par la structure documentaire ci-dessous)

  // === Corps : documentation Microsoft 365 (table des matières complète) ===
  const H1 = (tt: string) => new Paragraph({ text: tt, heading: HeadingLevel.HEADING_1, pageBreakBefore: true });
  const H3 = (tt: string) => new Paragraph({ text: tt, heading: HeadingLevel.HEADING_3 });
  const P  = (tt: string) => new Paragraph({ children: [new TextRun({ text: tt, size: 20 })] });
  const NOTE = (tt: string) => new Paragraph({ children: [new TextRun({ text: tt, italics: true, size: 18, color: '888888' })] });
  const KV = (rws: [string, string][]) => new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: rws.map(([k, v]) => new TableRow({ children: [headerCell(k, 34), cell(v)] })) });
  const ctrlsFam = (...fams: string[]) => controls.filter((c) => fams.includes(c.family));
  const ctrlsRe  = (re: RegExp) => controls.filter((c) => re.test(c.title) || re.test(c.id));
  const ctrlTbl  = (list: ControlDefinition[]): Paragraph | Table => list.length ? controlTable(list, scanByControl) : NOTE('Aucun contrôle automatisé rattaché — section à documenter/compléter manuellement.');
  const F = facts;
  const ch: (Paragraph | Table)[] = [];

  // 1. Introduction
  ch.push(H1('1. Introduction'));
  ch.push(H3('1.1 Objectifs du document'), P("Documenter la configuration et la posture de sécurité de la plateforme Microsoft 365, comme base de preuve pour la conformité NIS2 (art. 21) et pour l'exploitation courante."));
  ch.push(H3('1.2 Périmètre'), P(meta.tenantDomain ? `Tenant Microsoft 365 ${meta.tenantDomain}${meta.tenantId ? ` (${meta.tenantId})` : ''}, services associés et identités.` : 'Tenant Microsoft 365 connecté et services associés.'));
  ch.push(H3('1.3 Public cible'), P('RSSI, DPO, administrateurs Microsoft 365, auditeurs internes et externes.'));
  ch.push(H3('1.4 Références documentaires'), P('Directive NIS2 · RGPD · ISO/IEC 27001 · CCB CyberFundamentals (CyFun) · documentation Microsoft Learn.'));
  ch.push(H3('1.5 Historique des versions'), KV([['Version', '1.0 (auto-générée)'], ['Date', today()], ['Auteur', 'NIS365 Auditor — AutoDoc'], ['Dernier scan de référence', fmtDate(meta.lastScanDate) || '—']]));
  ch.push(P(`Périmètre évalué automatiquement : ${nCovered} éléments couverts (${nPass} conformes · ${nWarn} à améliorer · ${nFail} non conformes · ${nManual} à vérifier).`));

  // 2. Environnement
  ch.push(H1('2. Présentation de l’environnement Microsoft 365'));
  ch.push(H3('2.1 Vue d’ensemble de l’architecture'), P('Plateforme Microsoft 365 : Entra ID (identités), Exchange Online (messagerie), SharePoint / OneDrive (collaboration & stockage), Teams, et services de sécurité Microsoft Defender & Purview.'));
  ch.push(H3('2.2 Tenant Microsoft 365'), KV([
    ['Organisation', F?.organization?.displayName || meta.orgName || '—'],
    ['Domaine principal', meta.tenantDomain || '—'],
    ['Identifiant de tenant', meta.tenantId || F?.organization?.tenantId || '—'],
    ['Création du tenant', fmtDate(F?.organization?.createdDateTime) || '—'],
    ['Synchronisation AD locale', F?.organization ? (F.organization.onPremisesSyncEnabled ? 'Oui (hybride)' : 'Non (cloud only)') : '—'],
  ]));
  ch.push(H3('2.3 Licences utilisées'));
  if (F?.licenses?.items?.length) {
    ch.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [headerCell('SKU', 60), headerCell('Activées', 20), headerCell('Consommées', 20)] }),
      ...F.licenses.items.map((s) => new TableRow({ children: [cell(s.skuPartNumber || '—'), cell(String(s.enabled)), cell(String(s.consumed))] })),
    ] }));
  } else { ch.push(NOTE('Licences non récupérées (connexion Microsoft Graph requise).')); }
  ch.push(H3('2.4 Services activés'), P('Exchange Online, SharePoint Online, OneDrive, Teams, Entra ID — selon les licences ci-dessus.'));
  ch.push(H3('2.5 Régions d’hébergement des données'), P(F?.organization?.country ? `Pays du tenant : ${F.organization.country}. Résidence des données : Union européenne (à confirmer via Message Center / Data residency).` : 'Résidence des données à confirmer (Union européenne pour les tenants UE).'));

  // 3. Gouvernance
  ch.push(H1('3. Gouvernance'));
  ch.push(P("La gouvernance décrit comment les responsabilités de sécurité sont réparties et contrôlées. NIS2 exige une implication de la direction et une gestion rigoureuse des accès à privilèges, qui constituent la cible principale des attaquants. L'objectif est de limiter le nombre de comptes puissants, de tracer leur usage et de séparer les fonctions sensibles."));
  ch.push(H3('3.1 Organisation des rôles administratifs'), P(F?.globalAdmins?.count != null ? `Le tenant compte ${F.globalAdmins.count} Global Administrator(s). Microsoft recommande d'en limiter le nombre à 2–4, d'exiger une authentification forte et d'utiliser des rôles moins privilégiés (Security Admin, User Admin, etc.) pour les tâches courantes plutôt que le rôle Global Administrator.` : 'Recenser l’ensemble des rôles à privilèges (Global Admin, Security Admin, Exchange Admin, etc.) et leurs titulaires, et appliquer le principe du moindre privilège.'));
  ch.push(H3('3.2 Responsabilités des équipes'), NOTE('À compléter : matrice RACI des responsabilités de sécurité et d’exploitation.'));
  ch.push(H3('3.3 Séparation des privilèges'), P('Principe du moindre privilège, rôles dédiés, séparation des tâches. PIM recommandé pour l’élévation à la demande.'));
  ch.push(H3('3.4 Comptes d’administration'), NOTE('À compléter : liste et convention de nommage des comptes d’administration dédiés (non nominatifs pour les tâches à privilèges).'));
  ch.push(H3('3.5 Comptes d’urgence (Break Glass)'), P('Au moins deux comptes d’urgence exclus de l’accès conditionnel, à MFA fort, surveillés et testés régulièrement.'));

  // 4. Gestion des identités
  ch.push(H1('4. Gestion des identités'));
  ch.push(H3('4.1 Microsoft Entra ID'), P('Fournisseur d’identité central de la plateforme.'), ctrlTbl(ctrlsFam('identity')));
  ch.push(H3('4.2 Synchronisation Active Directory'), P(F?.organization ? (F.organization.onPremisesSyncEnabled ? 'Synchronisation AD locale active (environnement hybride).' : 'Pas de synchronisation AD locale (cloud only).') : 'État de synchronisation AD à confirmer.'));
  ch.push(H3('4.3 Authentification hybride'), NOTE('À compléter : méthode (PHS, PTA, ADFS) si environnement hybride.'));
  ch.push(H3('4.4 Gestion des utilisateurs'), P(F?.users?.total != null ? `Nombre total d’utilisateurs : ${F.users.total}.` : 'Processus de cycle de vie des comptes (création, modification, désactivation).'));
  ch.push(H3('4.5 Gestion des groupes'), NOTE('À compléter : conventions de nommage et gouvernance des groupes (sécurité, M365, dynamiques).'));
  ch.push(H3('4.6 Gestion des invités (B2B)'), P(F?.users?.guests != null ? `Comptes invités : ${F.users.guests}. Vérifier la politique d’accès externe et l’expiration/revue des invités.` : 'Politique d’accès externe et revue des invités à documenter.'));

  // 5. Authentification et sécurité
  ch.push(H1('5. Authentification et sécurité'));
  ch.push(P("Le contrôle d'accès est la première ligne de défense de la plateforme et un point d'attention majeur de NIS2. Il repose sur l'authentification multifacteur (MFA), idéalement résistante au phishing (passwordless / FIDO2 / Windows Hello), et sur l'accès conditionnel qui applique des règles contextuelles (utilisateur, appareil, localisation, risque) avant d'autoriser une session."));
  ch.push(H3('5.1 Authentification multifacteur (MFA)'), P('La MFA doit couvrir 100 % des utilisateurs, et en priorité les comptes à privilèges. Les méthodes fortes (application Authenticator avec number matching, FIDO2) sont préférées aux SMS.'), ctrlTbl(ctrlsRe(/\bmfa\b|multifacteur|multi-facteur|authentification forte/i)));
  ch.push(H3('5.2 Authentification Passwordless'), ctrlTbl(ctrlsRe(/passwordless|sans mot de passe|fido/i)));
  ch.push(H3('5.3 Windows Hello for Business'), NOTE('À compléter : déploiement de Windows Hello for Business.'));
  ch.push(H3('5.4 Accès conditionnel'));
  if (F?.conditionalAccess?.items?.length) {
    ch.push(P(`${F.conditionalAccess.count} politique(s) — ${F.conditionalAccess.enabled} active(s).`));
    ch.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [headerCell('Politique', 75), headerCell('État', 25)] }),
      ...F.conditionalAccess.items.map((p) => new TableRow({ children: [cell(p.displayName || '—'), cell(p.state === 'enabled' ? 'Activée' : p.state === 'disabled' ? 'Désactivée' : (p.state || '—'), { color: p.state === 'enabled' ? '1F7A45' : '7A7A7A', bold: true })] })),
    ] }));
  } else { ch.push(ctrlTbl(ctrlsRe(/conditional access|acc[eè]s conditionnel/i))); }
  ch.push(H3('5.5 Protection des identités'), ctrlTbl(ctrlsRe(/identity protection|risky|risque|pim|privileged identity/i)));
  ch.push(H3('5.6 Politiques de mot de passe'), NOTE('À compléter : politique de mot de passe / bannissement des mots de passe faibles (Password Protection).'));

  // 6. Gestion des appareils
  ch.push(H1('6. Gestion des appareils'));
  ch.push(H3('6.1 Microsoft Intune & conformité des appareils'), ctrlTbl(ctrlsFam('endpoint')));
  ch.push(H3('6.2 Enrôlement des appareils'), NOTE('À compléter : méthodes d’enrôlement (Autopilot, Company Portal).'));
  ch.push(H3('6.3 Protection des applications mobiles (MAM)'), NOTE('À compléter : politiques de protection des applications mobiles.'));

  // 7. Microsoft Defender
  ch.push(H1('7. Microsoft Defender'));
  ch.push(P("La suite Microsoft Defender constitue la couche de détection et de réponse de la plateforme, en réponse directe aux obligations NIS2 de détection, traitement et notification des incidents. Elle regroupe Defender for Office 365 (protection de la messagerie contre le phishing et les pièces jointes/liens malveillants), Defender for Endpoint (EDR sur les postes), Defender for Identity (détection des menaces sur Active Directory), Defender for Cloud Apps (CASB) et Microsoft XDR qui corrèle l'ensemble des signaux."));
  ch.push(P('Les contrôles automatisés ci-dessous reflètent l’état des politiques et fonctionnalités Defender détectées sur le tenant. Les composants nécessitant une licence E5 / add-on peuvent apparaître « non applicables » si non licenciés.'));
  ch.push(ctrlTbl(ctrlsRe(/defender|xdr|safe attachments|safe links|attack simulation|anti-?phish|anti-?spam|quarantine/i)));

  // 8. Sécurité des données (Purview)
  ch.push(H1('8. Sécurité des données (Microsoft Purview)'));
  ch.push(P("Microsoft Purview couvre la classification, la protection et la gouvernance des données à caractère personnel et sensible — un volet central pour NIS2 (protection des données) comme pour le RGPD. Il inclut les étiquettes de confidentialité (sensitivity labels), le chiffrement associé, la prévention des pertes de données (DLP) sur Exchange, SharePoint, OneDrive et Teams, ainsi que les politiques de rétention et de records management."));
  ch.push(P('Recommandations : définir une taxonomie de classification (Public / Interne / Confidentiel / Restreint), publier les étiquettes avec chiffrement pour les niveaux élevés, et activer au moins une politique DLP ciblant les données réglementées (PII, données bancaires).'));
  ch.push(ctrlTbl(ctrlsFam('data')));

  // 9. Messagerie Exchange Online
  ch.push(H1('9. Messagerie Exchange Online'));
  ch.push(P("Exchange Online est le service de messagerie de la plateforme. Sa sécurisation combine l'authentification des expéditeurs (SPF, DKIM, DMARC) pour lutter contre l'usurpation, les protections anti-spam et anti-phishing, la gestion des connecteurs SMTP, l'archivage et les politiques de rétention légales."));
  ch.push(H3('9.1 Authentification email (SPF / DKIM / DMARC) & anti-phishing'));
  ch.push(P('SPF déclare les serveurs autorisés à émettre pour le domaine, DKIM signe cryptographiquement les messages, et DMARC définit la politique appliquée en cas d’échec (quarantaine/rejet) tout en fournissant des rapports. Une configuration DMARC en p=reject est la cible recommandée.'));
  ch.push(ctrlTbl(ctrlsFam('email')));
  ch.push(H3('9.2 Archivage & rétention'), P('Documenter l’archivage (In-Place Archive), la mise en conservation légale (Litigation Hold) et les politiques de rétention Purview appliquées aux boîtes aux lettres, en cohérence avec les obligations légales de conservation.'));

  // 10. Microsoft Teams
  ch.push(H1('10. Microsoft Teams'));
  ch.push(P('Politiques de réunions, partage externe, gouvernance des équipes.'), ctrlTbl(ctrlsRe(/teams/i)));

  // 11. SharePoint Online
  ch.push(H1('11. SharePoint Online'));
  ch.push(ctrlTbl(ctrlsRe(/sharepoint/i)));

  // 12. OneDrive for Business
  ch.push(H1('12. OneDrive for Business'));
  ch.push(ctrlTbl(ctrlsRe(/onedrive/i)));

  // 13. Sauvegarde et restauration
  ch.push(H1('13. Sauvegarde et restauration'));
  ch.push(ctrlTbl(ctrlsRe(/backup|sauvegarde|restaur/i)));

  // 14. Journalisation et supervision
  ch.push(H1('14. Journalisation et supervision'));
  ch.push(ctrlTbl(ctrlsFam('logs')));

  // 15. Gestion des incidents
  ch.push(H1('15. Gestion des incidents'));
  ch.push(P("La gestion des incidents est une exigence explicite de NIS2 : les entités doivent détecter, qualifier, traiter et notifier les incidents significatifs dans des délais stricts (alerte précoce sous 24 h, notification sous 72 h, rapport final sous 1 mois). Le processus s'appuie sur les alertes Microsoft Defender / XDR et les journaux d'audit."));
  ch.push(H3('15.1 Détection & qualification'), P('Les incidents sont détectés via Defender XDR, les alertes Entra ID et la supervision des journaux. Chaque incident est qualifié (gravité, périmètre, données concernées) pour déterminer s’il est « significatif » au sens NIS2.'));
  ch.push(H3('15.2 Réponse, escalade & notification'), P('Définir les rôles (équipe de réponse), les canaux d’escalade, et les modèles de notification à l’autorité compétente (au niveau belge : le CCB). Conserver les preuves pour le rapport final.'));
  ch.push(H3('15.3 Retour d’expérience'), NOTE('À compléter : procédure de post-mortem et registre des incidents.'));

  // 16. Continuité d'activité
  ch.push(H1('16. Continuité d’activité'));
  ch.push(P("La continuité d'activité vise à garantir la disponibilité des services et la capacité de reprise après un incident majeur. Microsoft 365 fournit un SLA de disponibilité de 99,9 % et une redondance native, mais la protection contre la suppression/altération des données (sauvegarde) et l'organisation de la reprise restent de la responsabilité de l'organisation."));
  ch.push(H3('16.1 Plan de continuité (PCA) & de reprise (PRA)'), NOTE('À compléter : PCA/PRA, RTO/RPO cibles, procédures de bascule.'));
  ch.push(H3('16.2 Sauvegardes & tests'), P('Documenter la stratégie de sauvegarde (Microsoft 365 Backup ou solution tierce) couvrant Exchange, SharePoint, OneDrive et Teams, avec une rétention adaptée, et planifier des tests de restauration périodiques.'));

  // 17. Conformité
  ch.push(H1('17. Conformité'));
  ch.push(P("Ce chapitre relie la configuration technique aux cadres réglementaires applicables. NIS2 impose des mesures de gestion des risques et de notification ; le RGPD encadre le traitement des données personnelles ; ISO/IEC 27001 fournit un système de management de la sécurité de référence."));
  ch.push(H3('17.1 NIS2, RGPD & ISO 27001'), P('La correspondance détaillée des contrôles avec NIS2, ISO 27001 et le référentiel CCB CyberFundamentals (CyFun) est disponible dans le Rapport CyFun dédié, généré depuis l’onglet Rapport CyFun.'));
  ch.push(H3('17.2 Conservation & eDiscovery'), P('Documenter les politiques de conservation Purview et les capacités eDiscovery (Standard / Premium) mobilisables en cas de demande légale ou d’investigation.'));

  // 18. Procédures d'administration
  ch.push(H1('18. Procédures d’administration'));
  ch.push(P('Création d’utilisateur · attribution de licences · réinitialisation MFA · réinitialisation de mot de passe · création de groupe · création de boîte partagée.'), NOTE('À compléter : modes opératoires détaillés (captures / étapes).'));

  // 19. Maintenance
  ch.push(H1('19. Maintenance'));
  ch.push(P('Mise à jour des politiques, vérification des licences, contrôle des accès, revue des privilèges, gestion des certificats/secrets (voir annexe App Registrations).'));

  // 20. Documentation technique
  ch.push(H1('20. Documentation technique'));
  ch.push(P(F?.appRegistrations ? `Dépendances applicatives : ${F.appRegistrations.count} App Registration(s) intégrées (détail en annexe).` : 'Architecture réseau, flux d’authentification, diagrammes et dépendances à documenter.'), NOTE('À compléter : diagrammes d’architecture et flux d’authentification.'));

  // 21. Annexes (glossaire, contacts, inventaire des applications intégrées)
  ch.push(H1('21. Annexes'));
  ch.push(H3('21.1 Glossaire & acronymes'), P('MFA : authentification multifacteur · CA : accès conditionnel · DLP : prévention des pertes de données · PIM : Privileged Identity Management · XDR : détection & réponse étendues.'));
  ch.push(H3('21.2 Contacts utiles'), NOTE('À compléter : RSSI, DPO, support IT.'));
  ch.push(H3('21.3 Liens Microsoft'), P('Microsoft Learn · Microsoft 365 Admin Center · Entra admin center · Microsoft Purview · Microsoft Defender portal.'));
  ch.push(H3('21.4 Inventaire des applications intégrées'), NOTE('Détail (App Registrations & expiration des secrets) ci-après.'));

  const doc = new Document({
    creator: 'NIS365 Auditor',
    title: `Documentation Plateforme Microsoft 365 - ${meta.orgName}`,
    features: { updateFields: true },
    styles: {
      default: { document: { run: { size: 20 } } },
      paragraphStyles: [
        { id: 'Heading1', name: 'Heading 1', basedOn: 'Normal', next: 'Normal', run: { size: 30, bold: true, color: BRAND }, paragraph: { spacing: { before: 240, after: 120 } } },
        { id: 'Heading3', name: 'Heading 3', basedOn: 'Normal', next: 'Normal', run: { size: 24, bold: true, color: ACCENT }, paragraph: { spacing: { before: 160, after: 80 } } },
      ],
    },
    sections: [{ children: [...cover, ...buildToc(), ...ch, ...(facts ? buildGraphAnnex(facts) : [])] }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `Doc_Plateforme_M365_${safeName(meta.orgName)}_${new Date().toISOString().slice(0, 10)}.docx`);
}
